import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/pages/hotel-intake.css';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

const FOOD_TYPE_LABELS = { kibble: 'Croqueta', wet: 'Húmedo', raw: 'BARF', homemade: 'Casero', mixed: 'Mixto' };

const FEEDINGS = [
  { key: 'feedingMorning',   label: 'Mañana' },
  { key: 'feedingAfternoon', label: 'Tarde'  },
  { key: 'feedingEvening',   label: 'Noche'  },
];

function formatDate(d) {
  if (!d) return '';
  const s = typeof d === 'string' ? d : new Date(d).toISOString();
  const [y, m, day] = s.slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
}

// One dog's saved answers, or a note that the form is still pending.
function DogIntakeSummary({ f }) {
  if (!f?.submittedAt) {
    return (
      <div className="is-no-form">
        <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
        <p>El formulario de ingreso aún no ha sido completado.</p>
      </div>
    );
  }

  const feedings = FEEDINGS.filter(({ key }) => f[key]?.time || f[key]?.amount || f[key]?.notes);
  const foodTypes = (f.foodTypes || []).map(t => FOOD_TYPE_LABELS[t] || t).join(', ');
  
  const hasMedications = f.medications && f.medications.length > 0;
  const belongingsEntries = f.belongings ? Object.entries(f.belongings).filter(([_, b]) => b.included) : [];

  return (
    <>
      <div className="is-section">
        <div className="is-section-title">🍽️ Alimentación</div>

        {(f.foodBrand || foodTypes || f.foodQuantity) && (
          <div className="is-food-meta">
            {f.foodBrand && <span className="is-food-tag">{f.foodBrand}</span>}
            {foodTypes   && <span className="is-food-tag">{foodTypes}</span>}
            {f.foodQuantity && <span className="is-food-tag">{f.foodQuantity}</span>}
          </div>
        )}

        {feedings.length > 0 ? (
          <div className="is-feeding-cards">
            {feedings.map(({ key, label }) => {
              const fd = f[key];
              return (
                <div key={key} className="is-feeding-card">
                  <div className="is-feeding-card-period">{label}</div>
                  {fd.time   && <div className="is-feeding-card-row"><span className="is-feeding-card-lbl">Hora</span><span>{fd.time}</span></div>}
                  {fd.amount && <div className="is-feeding-card-row"><span className="is-feeding-card-lbl">Cantidad</span><span>{fd.amount}</span></div>}
                  {fd.notes  && <div className="is-feeding-card-notes">{fd.notes}</div>}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="is-none-tag">Sin horarios registrados</div>
        )}

        {f.dietaryRestrictions && (
          <div className="is-restrictions">
            <div className="is-restrictions-label">⚠️ Alergias / Restricciones alimenticias</div>
            {f.dietaryRestrictions}
          </div>
        )}
      </div>

      {/* Medicamentos */}
      <div className="is-section">
        <div className="is-section-title">💊 Salud y Medicamentos</div>
        {f.noMedications ? (
          <div className="is-none-tag">No toma medicamentos</div>
        ) : hasMedications ? (
          <div className="is-feeding-cards">
            {f.medications.map((m, i) => (
              <div key={i} className="is-feeding-card">
                <div className="is-feeding-card-period">{m.name}</div>
                {m.dose && <div className="is-feeding-card-row"><span className="is-feeding-card-lbl">Dosis</span><span>{m.dose}</span></div>}
                {m.schedule && <div className="is-feeding-card-row"><span className="is-feeding-card-lbl">Horario</span><span>{m.schedule}</span></div>}
                {m.notes && <div className="is-feeding-card-notes">{m.notes}</div>}
              </div>
            ))}
          </div>
        ) : (
          <div className="is-none-tag">Sin información de medicamentos</div>
        )}
        {f.healthNotes && (
          <div className="is-restrictions" style={{ marginTop: 12 }}>
            <div className="is-restrictions-label">🏥 Notas de salud adicionales</div>
            {f.healthNotes}
          </div>
        )}
      </div>

      {/* Comportamiento */}
      <div className="is-section">
        <div className="is-section-title">🐕 Comportamiento y Hábitos</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <strong>Sociable con perros:</strong> {f.socialWithDogs === true ? 'Sí' : f.socialWithDogs === false ? 'No' : 'No especificado'}
          </div>
          <div>
            <strong>Puede estar solo:</strong> {f.canStayAlone === true ? 'Sí' : f.canStayAlone === false ? 'No' : 'No especificado'}
          </div>
          <div>
            <strong>Nivel de energía:</strong> {f.energyLevel || 'No especificado'}
          </div>
        </div>
        
        {(f.fears && f.fears.length > 0) && (
          <div style={{ marginTop: 16 }}>
            <strong>Miedos:</strong> {f.fears.join(', ')} {f.fearsOther && `(${f.fearsOther})`}
          </div>
        )}
        
        {(f.behaviors && f.behaviors.length > 0) && (
          <div style={{ marginTop: 16 }}>
            <strong>Comportamientos:</strong> {f.behaviors.join(', ')} {f.behaviorsOther && `(${f.behaviorsOther})`}
          </div>
        )}
      </div>

      {/* Pertenencias */}
      <div className="is-section">
        <div className="is-section-title">🎒 Pertenencias</div>
        {belongingsEntries.length > 0 ? (
          <div className="is-feeding-cards">
            {belongingsEntries.map(([key, item]) => (
              <div key={key} className="is-feeding-card">
                <div className="is-feeding-card-period" style={{ textTransform: 'capitalize' }}>{key}</div>
                <div className="is-feeding-card-row"><span className="is-feeding-card-lbl">Cantidad</span><span>{item.quantity}</span></div>
                {item.notes && <div className="is-feeding-card-notes">{item.notes}</div>}
              </div>
            ))}
          </div>
        ) : (
          <div className="is-none-tag">No trajo pertenencias</div>
        )}
      </div>
      
      {/* Contacto Emergencia */}
      {(f.emergencyContactName || f.emergencyContactPhone) && (
        <div className="is-section">
          <div className="is-section-title">☎️ Contacto de Emergencia</div>
          <div><strong>Nombre:</strong> {f.emergencyContactName || '-'}</div>
          <div><strong>Teléfono:</strong> {f.emergencyContactPhone || '-'}</div>
        </div>
      )}
    </>
  );
}

export default function HotelIntakeSummaryPage() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!bookingId) return;
    fetch(`${API_URL}/api/hotel-intake/${bookingId}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setBooking)
      .catch(() => setError('No se pudo cargar la información de la reserva.'));
  }, [bookingId]);

  // Every dog of the reservation; a single-dog booking is a list of one.
  const dogs = booking?.dogs?.length
    ? booking.dogs
    : booking ? [{ dogName: booking.dogName, intakeForm: booking.intakeForm }] : [];
  const dogNames = dogs.map(d => d.dogName).join(', ');

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: `Comidas · ${dogNames}`, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url);
      alert('Enlace copiado al portapapeles');
    }
  };

  if (error)    return <div className="intake-wrap"><div className="intake-error">{error}</div></div>;
  if (!booking) return <div className="intake-wrap"><div className="intake-loading">Cargando…</div></div>;

  return (
    <div className="intake-wrap">
      <div className="is-header">
        <div className="intake-header-logo">🐾 Happy Tails Hotel</div>
        <div className="is-header-top">
          <div>
            <div className="is-dog-name">{dogNames}</div>
            <div className="is-dates">{formatDate(booking.checkIn)} → {formatDate(booking.checkOut)}</div>
          </div>
          <button className="is-share-btn" onClick={handleShare}>📤 Compartir</button>
        </div>
      </div>

      <div className="is-body">
        {dogs.map((dog, idx) => (
          <React.Fragment key={dog.token || idx}>
            {dogs.length > 1 && (
              <div className="is-dog-banner">
                <span className="intake-dog-banner-name">🐕 {dog.dogName}</span>
              </div>
            )}
            <DogIntakeSummary f={dog.intakeForm} />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
